import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

export default function StillWorkingBanner({ endpointCount, errorCount, authType, startedAt }) {
    const { t } = useTranslation();
    const elapsed = useElapsed(startedAt);
    const stats = [];
    if (endpointCount > 0) stats.push(t("builder.banner.endpointsFound", { count: endpointCount, defaultValue: "{{count}} endpoints" }));
    if (authType) stats.push(t("builder.banner.authDetected", { type: authType, defaultValue: "auth: {{type}}" }));
    if (errorCount > 0) stats.push(t("builder.banner.errorsMapped", { count: errorCount, defaultValue: "{{count}} errors mapped" }));

    return (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="relative shrink-0">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Icon name="Sparkles" className="w-4 h-4 text-white" />
                </div>
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full ring-2 ring-white animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900">{t("builder.banner.stillWorking", { defaultValue: "AI is still working" })}</p>
                    <span className="flex items-center gap-0.5">
                        <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                    {elapsed && (
                        <span className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 font-mono tabular-nums bg-white/60 px-2 py-0.5 rounded-full border border-blue-100">
                            <Icon name="Clock" className="w-3 h-3" />
                            {elapsed}
                        </span>
                    )}
                </div>
                {stats.length > 0 && (
                    <p className="text-xs text-gray-600 truncate mt-0.5">
                        {t("builder.banner.progressSoFar", { defaultValue: "Progress so far" })}: {stats.join(" · ")}
                    </p>
                )}
            </div>
            <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin shrink-0" />
        </div>
    );
}

function useElapsed(startedAt) {
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        if (!startedAt) return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [startedAt]);
    if (!startedAt) return null;
    const totalSec = Math.max(0, Math.floor((now - startedAt) / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}
